/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { IContract_Order, IContract_Post } from "@/interfaces/contract";
import RestAPIClient from "@/repo/restApi";
import {
  getContract,
  switchNetwork,
  getNetwork,
  configureChains,
  getWalletClient,
  WalletClient,
  PublicClient,
} from "@wagmi/core";
import { jsonRpcProvider } from "@wagmi/core/providers/jsonRpc";
import { decodeEventLog } from "viem";
import { makeAutoObservable, runInAction } from "mobx";

import { abi } from "./abi";
import { BigNumber, ethers, providers } from "ethers";
import gql from "@/repo/apolloClient";
import { POST_STATUS } from "@/@types/EnumType";
import { UI_ACTION_STATE } from "../common";
import { chains as Hapchains } from "../../web3modal/index";
const IS_TESTNET = process.env.REACT_APP_IS_TESTNET === "true";
const JSONRPC = process.env.REACT_APP_JSONRPC_ENDPOINT || "";
const CHAIN_ID = IS_TESTNET ? 373737 : 8794598;

interface State {
  myPosts: IContract_Post[];
  myOrders: IContract_Order[];
  address: string;
  singer: ethers.providers.JsonRpcSigner | undefined;
  assetPrices: IAssetPrice[];
  lastNonce: string;
}
export interface IAssetPrice {
  address: string;
  price: BigNumber;
}
export interface CT_Order {
  postId: BigNumber;
  buyer: string;
  tokenAmount: BigNumber;
  isLocked: boolean;
  admin: string;
  status: number;
  createdAt: BigNumber;
}
export interface CT_POST {
  postor: string;
  tokenAddress: string;
  tokenAmount: BigNumber;
  price: BigNumber;
  minOrder: BigNumber;
  maxOrder: BigNumber;
  isSell: boolean;
  isLocked: boolean;
  fiatCurrency: string;
  status: number;
}
export function walletClientToSigner(walletClient: WalletClient) {
  const { account, chain, transport } = walletClient;
  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  };
  const provider = new providers.Web3Provider(transport, network);
  const signer = provider.getSigner(account.address);
  return signer;
}
export async function getEthersSigner({ chainId }: { chainId?: number } = {}) {
  const walletClient = await getWalletClient({ chainId });
  if (!walletClient) return undefined;
  return walletClientToSigner(walletClient);
}
export default class MarketContractStore {
  restApi: RestAPIClient<unknown>;
  client: PublicClient<any>;
  state: State = {
    myPosts: [],
    myOrders: [],
    address: "",
    singer: undefined,
    assetPrices: [],
    lastNonce: "",
  };
  constructor() {
    this.restApi = new RestAPIClient("cryptoMarketPlace");
    makeAutoObservable(this);
    const { publicClient } = configureChains(Hapchains, [
      jsonRpcProvider({
        rpc: () => ({
          http: JSONRPC,
        }),
      }),
    ]);
    this.client = publicClient as any;
  }
  instance = async () => {
    const MAX_RETRY_ATTEMPTS = 1;
    const RETRY_DELAY_MS = 1000;
    let retryCount = 0;
    this.state.singer = await getEthersSigner({ chainId: CHAIN_ID });
    let signerOrProvider: any;

    while (!signerOrProvider && retryCount < MAX_RETRY_ATTEMPTS) {
      retryCount++;
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      signerOrProvider = this.state.singer;
    }

    return getContract({
      address: (process.env.REACT_APP_CONTRACT_ADDRESS || "") as any,
      abi: abi,
      walletClient: this.client,
    });
  };
  setNetwork = async () => {
    const network = await getNetwork();
    if (network.chain?.id !== (IS_TESTNET ? 373737 : 8794598)) {
      await switchNetwork({
        chainId: IS_TESTNET ? 373737 : 8794598,
      });
    }
  };
  fetchAssetPrice = async (tokenAddress: string): Promise<BigNumber> => {
    await this.setNetwork();
    const contract = await this.instance();
    try {
      let lastPrice = (await contract.read.lastPriceUSD([
        tokenAddress as any,
      ])) as any;
      lastPrice = ethers.BigNumber.from(lastPrice);
      runInAction(() => {
        if (
          !this.state.assetPrices.find(
            (asset) => asset.address === tokenAddress,
          )
        ) {
          this.state.assetPrices.push({
            address: tokenAddress,
            price: lastPrice as BigNumber,
          });
        } else {
          this.state.assetPrices = this.state.assetPrices.map((asset) => {
            if (asset.address === tokenAddress) {
              asset.price = lastPrice as BigNumber;
            }
            return asset;
          });
        }
      });
      return lastPrice as BigNumber;
    } catch (error) {
      console.error(error);
    }
    return ethers.BigNumber.from(0);
  };

  // setSigner = (_: any) => {
  //   // this.state.singer = signer;
  // };
  setAddress = async (address: string) => {
    this.state.address = address;
  };
  approveToken = async (
    tokenAddress: string,
    amount: string,
    spender: string,
  ): Promise<string> => {
    const contract = getContract({
      address: tokenAddress as any,
      abi: [
        {
          inputs: [
            {
              internalType: "address",
              name: "spender",
              type: "address",
            },
            {
              internalType: "uint256",
              name: "amount",
              type: "uint256",
            },
          ],
          name: "approve",
          outputs: [
            {
              internalType: "bool",
              name: "",
              type: "bool",
            },
          ],
          stateMutability: "nonpayable",
          type: "function",
        },
      ],
      walletClient: this.state.singer,
    });
    //@ts-ignore
    const response = await contract.approve(spender, amount);
    await response.wait(3);
    return response.hash;
  };

  createPost = async (
    post: IContract_Post,
    cb?: (result: UI_ACTION_STATE, message?: string) => void,
  ): Promise<string> => {
    await this.setNetwork();
    await this.instance();
    const nonce = await this.getNonce();
    const message = ethers.utils.solidityKeccak256(
      [
        "address",
        "uint256",
        "uint256",
        "uint256",
        "uint256",
        "bool",
        "string",
        "address",
        "uint256",
      ],
      [
        post.tokenAddress,
        post.tokenAmount,
        post.price,
        post.minOrder,
        post.maxOrder,
        post.isSell,
        post.fiatCurrency,
        this.state.address,
        nonce,
      ],
    );
    const hash = ethers.utils.arrayify(message);
    const result = await this.state.singer?.signMessage(hash || "");

    if (cb && typeof cb === "function") {
      cb(UI_ACTION_STATE.TRANSACTION_PENDING);
    }
    const response = await this.restApi.post({
      method: "createPost",
      request: {
        title: post.title,
        token_address: post.tokenAddress,
        token_amount: post.tokenAmount,
        price: post.price,
        min_amount: post.minOrder,
        max_amount: post.maxOrder,
        is_sell: post.isSell,
        fiat_currency: post.fiatCurrency,
        nonce: nonce,
        wallet_address: this.state.address,
        post_type: post.post_type,
        payment_method: post.payment_method,
        asset_id: post.asset_id,
        signature: result,
        description: post.description,
      },
    });
    return response.data;
  };
  private changePostStatus = async (
    postId: string,
    status: POST_STATUS,
    cb?: (result: UI_ACTION_STATE, message?: string) => void,
  ) => {
    await this.instance();
    await this.setNetwork();
    const nonce = await this.getNonce();
    let _statusInt = 0;
    switch (status) {
      case POST_STATUS.ACTIVE:
        _statusInt = 1;
        break;
      case POST_STATUS.INACTIVE:
        _statusInt = 2;
        break;
      case POST_STATUS.CANCELLED:
        _statusInt = 3;
        break;
    }
    const message = ethers.utils.solidityKeccak256(
      ["uint256", "address", "uint8", "uint256"],
      [postId, this.state.address, _statusInt, nonce],
    );
    const hash = ethers.utils.arrayify(message);
    const result = await this.state.singer?.signMessage(hash || "");
    if (cb && typeof cb === "function") {
      cb(UI_ACTION_STATE.TRANSACTION_PENDING);
    }
    const response = await this.restApi.post({
      method: "changePostStatus",
      request: {
        post_id: postId,
        status: _statusInt,
        nonce: nonce,
        wallet_address: this.state.address,
        signature: result,
      },
    });
    const queryUpdatePostStatus = `
      mutation updatePostStatus($postId: String!, $status: String!) {
        update_posts(where: {contract_post_id: {_eq:$postId}}, _set: {status: $status}) {
          affected_rows
        }
      }
    `;
    await gql.Mutation(queryUpdatePostStatus, {
      postId: postId,
      status: status,
    });
    runInAction(() => {
      this.state.lastNonce = nonce.toString();
    });
    return response.data;
  };
  cancelPost = async (
    postId: string,
    cb?: (result: UI_ACTION_STATE, message?: string) => void,
  ): Promise<string> => {
    return this.changePostStatus(postId, POST_STATUS.CANCELLED, cb);
  };
  activatePost = async (
    postId: string,
    cb?: (result: UI_ACTION_STATE, message?: string) => void,
  ): Promise<string> => {
    return this.changePostStatus(postId, POST_STATUS.ACTIVE, cb);
  };
  deactivatePost = async (
    postId: string,
    cb?: (result: UI_ACTION_STATE, message?: string) => void,
  ): Promise<string> => {
    return this.changePostStatus(postId, POST_STATUS.INACTIVE, cb);
  };

  createOrder = async (
    order: IContract_Order,
    cb?: (result: UI_ACTION_STATE, message?: string) => void,
  ): Promise<string> => {
    const contract = await this.instance();

    await this.setNetwork();
    const post = (await contract.read.posts([order.postId as any])) as any;
    if (!post) {
      throw new Error("Post not found");
    }

    const nonce = await this.getNonce();
    const message = ethers.utils.solidityKeccak256(
      ["uint256", "uint256", "address", "uint256"],
      [order.postId, order.tokenAmount, this.state.address, nonce],
    );
    const hash = ethers.utils.arrayify(message);
    const result = await this.state.singer?.signMessage(hash || "");
    if (cb && typeof cb === "function") {
      cb(UI_ACTION_STATE.TRANSACTION_PENDING);
    }
    const response = await this.restApi.post({
      method: "createOrder",
      request: {
        post_id: order.postId,
        token_amount: order.tokenAmount,
        buyer_address: this.state.address,
        nonce: nonce,
        signature: result,
      },
    });
    runInAction(() => {
      this.state.lastNonce = nonce.toString();
    });
    return response.data;
  };
  confirmOrder = async (
    orderId: string,
    cb?: (result: UI_ACTION_STATE, message?: string) => void,
  ): Promise<string> => {
    const contract = await this.instance();
    await this.setNetwork();
    const order = (await contract.read.orders([orderId as any])) as any;
    if (!order) {
      throw new Error("Order not found");
    }
    const [postor] = (await contract.read.posts([order[0] as any])) as any;
    if (!postor) {
      throw new Error("Post not found");
    }

    if (order[1] !== this.state.address && postor !== this.state.address) {
      throw new Error("You are not allowed to confirm this order");
    }
    const nonce = await this.getNonce();
    const message = ethers.utils.solidityKeccak256(
      ["uint256", "address", "uint256"],
      [orderId, this.state.address, nonce.toString()],
    );
    const hash = ethers.utils.arrayify(message);
    const result = await this.state.singer?.signMessage(hash || "");
    if (cb && typeof cb === "function") {
      cb(UI_ACTION_STATE.TRANSACTION_PENDING);
    }
    const response = await this.restApi.post({
      method: "confirmOrder",
      request: {
        order_id: orderId,
        paticipant_address: this.state.address,
        nonce: nonce.toString(),
        signature: result,
      },
    });
    runInAction(() => {
      this.state.lastNonce = nonce.toString();
    });
    return response.data;
  };
  cancelOrder = async (
    orderId: string,
    cb?: (result: UI_ACTION_STATE, message?: string) => void,
  ): Promise<string> => {
    const contract = await this.instance();
    await this.setNetwork();
    const order = (await contract.read.orders([orderId as any])) as any;
    if (!order) {
      throw new Error("Order not found");
    }
    const orderCreatedAt = BigNumber.from(order[6]).toNumber();
    const now = Math.floor(Date.now() / 1000);

    if (now - orderCreatedAt < 3600) {
      throw new Error("You cannot cancel this order");
    }
    const post = (await contract.read.posts([order[0] as any])) as any;
    if (order[1] !== this.state.address && post[0] !== this.state.address) {
      throw new Error("You are not allowed to cancel this order");
    }
    const nonce = await this.getNonce();
    const message = ethers.utils.solidityKeccak256(
      ["uint256", "address", "uint256"],
      [orderId, this.state.address, nonce.toString()],
    );
    const hash = ethers.utils.arrayify(message);
    const result = await this.state.singer?.signMessage(hash || "");
    if (cb && typeof cb === "function") {
      cb(UI_ACTION_STATE.TRANSACTION_PENDING);
    }
    const response = await this.restApi.post({
      method: "cancelOrder",
      request: {
        order_id: orderId,
        paticipant_address: this.state.address,
        nonce: nonce.toString(),
        signature: result,
      },
    });
    runInAction(() => {
      this.state.lastNonce = nonce.toString();
    });
    return response.data;
  };
  createMsgAppeal = async (
    contract_order_id: string,
  ): Promise<{
    contract_order_id: string;
    nonce: string;
    requestor_address: string;
    signature: string;
  }> => {
    await this.instance();
    await this.setNetwork();
    const nonce = await this.getNonce(true);
    const message = ethers.utils.solidityKeccak256(
      ["uint256", "address", "uint256"],
      [contract_order_id, this.state.address, nonce.toString()],
    );
    const hash = ethers.utils.arrayify(message);
    const result = await this.state.singer?.signMessage(hash || "");

    return {
      contract_order_id,
      nonce: nonce.toString(),
      requestor_address: this.state.address,
      signature: result || "",
    };
  };

  // get log data from transaction hash
  getLogData = async (txHash: string): Promise<any> => {
    await this.instance();
    await this.setNetwork();
    const response = await this.client.getTransactionReceipt({
      hash: txHash as any,
    });
    console.log(response);
    //decode log data

    // const logs = contract. .interface.parseLog(response.logs[0]);
    const logs = decodeEventLog({
      abi: abi,
      data: response.logs[0].data,
      topics: response.logs[0].topics,
    });
    return logs;
  };
  getOrderId = async (txHash: string): Promise<string> => {
    const logs = await this.getLogData(txHash);
    const orderId = logs.args.orderId.toString();
    return orderId;
  };
  getPostId = async (txHash: string): Promise<string> => {
    const logs = await this.getLogData(txHash);
    const postId = logs.args.postId.toString();
    return postId;
  };
  getPost = async (contract_post_id: string): Promise<IContract_Post> => {
    const contract = await this.instance();
    // await this.setNetwork();
    const result = (await contract.read.posts([
      contract_post_id as any,
    ])) as any;
    const post: IContract_Post = {
      postor: result[0],
      tokenAddress: result[1],
      tokenAmount: result[2].toString(),
      price: result[3].toString(),
      minOrder: result[4].toString(),
      maxOrder: result[5].toString(),
      isSell: result[6].toString(),
      isLocked: result[7],
      fiatCurrency: result[8],
      status: result[9].toString(),
      payment_method: "",
      post_type: "",
      title: "",
      isNativeToken: false,
    };
    return post;
  };

  getNonce = async (skipLastNone?: boolean): Promise<string> => {
    await this.setNetwork();
    let nonce = (await (
      await this.instance()
    ).read.getNonce([this.state.address as any])) as any;
    nonce = ethers.BigNumber.from(nonce);
    if (skipLastNone) {
      return nonce.toString();
    }
    if (this.state.lastNonce == "") {
      return nonce.toString();
    }
    for (let i = 0; i < 10; i++) {
      if (nonce.toString() === this.state.lastNonce) {
        // nonce is not updated yet, wait for 1 second
        await new Promise((resolve) => setTimeout(resolve, 3000));
      }
      nonce = (await (
        await this.instance()
      ).read.getNonce([this.state.address as any])) as any;
      nonce = ethers.BigNumber.from(nonce);
      if (nonce.toString() === this.state.lastNonce) {
        continue;
      }

      return nonce.toString();
    }
    throw new Error("Cannot get nonce");
  };
  get assetPrices() {
    return this.state.assetPrices;
  }
}
